import { useNavigation, useShow, type HttpError } from "@refinedev/core";

import type { ActionCard } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { JsonViewer } from "@/components/app/JsonViewer";
import { formatDateTime, humanize } from "@/lib/format";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  TechnicalId,
  type RelationGroup,
} from "@/components/inspector";
import { ShowPageError, ShowPageSkeleton } from "@/components/inspector/ShowPage";

import { ActionCardLifecycleActions } from "./ActionCardLifecycleActions";
import { formatScore, parseJsonText } from "./model";

function nullableText(value: string | null | undefined) {
  return value && value.trim() ? value : "-";
}

function relations(card: ActionCard): RelationGroup[] {
  return [
    {
      title: "Context and subject",
      items: [
        {
          label: "Primary context",
          resource: "contexts",
          id: card.context_id,
        },
        {
          label: humanize(card.subject_type),
          resource: card.subject_type === "context" ? "contexts" : undefined,
          id: card.subject_type === "context" ? card.subject_id : undefined,
          node:
            card.subject_type === "context" || !card.subject_id ? undefined : (
              <span className="font-mono text-xs">{card.subject_id}</span>
            ),
        },
      ],
    },
    {
      title: "Trace",
      emptyMessage: "No trace links have been attached yet.",
      items: [
        {
          label: "Source event",
          resource: "events",
          id: card.source_event_id ?? undefined,
        },
        {
          label: "Rule run",
          resource: "rule-runs",
          id: card.rule_run_id ?? undefined,
        },
        {
          label: "Agent run",
          resource: "agent-runs",
          id: card.agent_run_id ?? undefined,
        },
        {
          label: "Approval request",
          node: card.approval_request_id ? (
            <span className="font-mono text-xs">{card.approval_request_id}</span>
          ) : undefined,
        },
        {
          label: "Decision",
          resource: "decisions",
          id: card.decision_id ?? undefined,
        },
      ],
    },
  ];
}

export function ActionCardShow() {
  const { query } = useShow<ActionCard, HttpError>({ resource: "action-cards" });
  const { list } = useNavigation();
  const card = query.data?.data;

  if (query.isLoading) return <ShowPageSkeleton />;
  if (query.isError || !card) {
    return (
      <ShowPageError
        message={query.error?.message ?? "Action card not found."}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title={card.title}
        subtitle="A durable proposal. Accepting it routes back through policy, approval, events, and trace."
        status={card.status}
        primaryAction={
          <ActionCardLifecycleActions
            card={card}
            onChanged={() => void query.refetch()}
          />
        }
        actions={
          <Button variant="ghost" size="sm" onClick={() => list("action-cards")}>
            Back
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Type">{humanize(card.card_type)}</Field>
          <Field label="Category">{humanize(card.category)}</Field>
          <Field label="Severity">
            <Badge variant="secondary">{humanize(card.severity)}</Badge>
          </Field>
          <Field label="Risk">
            <Badge variant="outline">{humanize(card.risk_level)}</Badge>
          </Field>
          <Field label="Confidence">{formatScore(card.confidence)}</Field>
          <Field label="Priority score">{formatScore(card.priority_score)}</Field>
          <Field label="Suggested action">
            {humanize(card.suggested_action_type)}
          </Field>
          <Field label="Approval mode">{humanize(card.approval_mode)}</Field>
          <Field label="Context">
            <RefLink resource="contexts" id={card.context_id} />
          </Field>
          <Field label="Subject">
            {card.subject_type === "context" ? (
              <RefLink resource="contexts" id={card.subject_id} />
            ) : (
              <span className="font-mono text-xs">
                {card.subject_id ?? humanize(card.subject_type)}
              </span>
            )}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Reason">
        <div className="flex flex-col gap-3 text-sm">
          {card.summary ? (
            <p className="text-foreground">{card.summary}</p>
          ) : null}
          <p className="whitespace-pre-wrap text-muted-foreground">
            {card.reason}
          </p>
        </div>
      </InspectorSection>

      <InspectorSection title="Action Preview">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Command
            </h4>
            <JsonViewer data={parseJsonText(card.command_preview_json)} />
          </div>
          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Expected effect
            </h4>
            <JsonViewer data={parseJsonText(card.expected_effect_json)} />
          </div>
        </div>
      </InspectorSection>

      <InspectorSection title="Lifecycle">
        <FieldGrid>
          <Field label="Generated by">
            {humanize(card.generated_by_type)} ·{" "}
            <span className="font-mono text-xs">{card.generated_by_id}</span>
          </Field>
          <Field label="Occurrences">{card.occurrence_count}</Field>
          <Field label="First seen">{formatDateTime(card.first_seen_at)}</Field>
          <Field label="Last seen">{formatDateTime(card.last_seen_at)}</Field>
          <Field label="Deferred until">{formatDateTime(card.defer_until)}</Field>
          <Field label="Deferred reason">{nullableText(card.defer_reason)}</Field>
          <Field label="Rejected reason">{nullableText(card.reject_reason)}</Field>
          <Field label="Execution status">
            {nullableText(card.execution_status)}
          </Field>
          <Field label="Execution error">
            {nullableText(card.execution_error)}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock groups={relations(card)} />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={card.id} />
          </Field>
          <Field label="Dedupe key">
            <span className="font-mono text-xs">{card.dedupe_key}</span>
          </Field>
          <Field label="Created">{formatDateTime(card.created_at)}</Field>
          <Field label="Updated">{formatDateTime(card.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock
          entityType="action-cards"
          entityId={card.id}
          provenance={[
            { label: "Context", resource: "contexts", id: card.context_id },
            { label: "Source event", resource: "events", id: card.source_event_id },
            { label: "Rule run", resource: "rule-runs", id: card.rule_run_id },
            { label: "Agent run", resource: "agent-runs", id: card.agent_run_id },
          ]}
        />
      </InspectorSection>
    </Inspector>
  );
}

export default ActionCardShow;
